﻿interface IDdlBinding {
    dropdownID: string;
    proxyID: string;
    anchorID: string;
    dotNetRef: DotNet.DotNetObject;
    failCount: number;
}

export class ZenDropdown {
    private observer: ResizeObserver;
    private boundDropdowns: IDdlBinding[] = [];

    constructor() {
        this.observer = new ResizeObserver(() => this.onResize());
    }

    private onResize() {
        for(const binding of this.boundDropdowns) {
            const dropdown = document.getElementById(binding.dropdownID);
            const proxy = document.getElementById(binding.proxyID);
            const anchor = document.getElementById(binding.anchorID);

            if (!proxy) {
                throw Error(`Could not find dropdown proxy with ID #${binding.proxyID}`);
            }

            if (!anchor) {
                throw Error(`Could not find dropdown anchor with ID #${binding.anchorID}`);
            }

            if (!dropdown) {
                ++binding.failCount;
                let errMsg = `Tried to resize dropdown ${binding.dropdownID} but no element with that ID could be found.`;
                if (binding.failCount >= 3) {
                    errMsg += ` Retry count exceeded - Unbinding dropdown.`;
                    this.unbindDropdown(binding.dropdownID);
                }
                console.warn(errMsg);
                continue;
            }

            this.handleDdlResize(binding, proxy, anchor, dropdown);
        }
    }

    private async handleDdlResize(binding: IDdlBinding, proxy: HTMLElement, anchor: HTMLElement, dropdown: HTMLElement) {
        const proxyRect = proxy.getBoundingClientRect();
        const elRect = anchor.getBoundingClientRect();
        const ddlRect = dropdown.getBoundingClientRect();

        let left = elRect.left - proxyRect.left;
        let top = elRect.top - proxyRect.top;
        let width = elRect.width;

        // Check to see if the ddl will breach the viewport
        let ddlRight = left + ddlRect.width;
        let ddlBottom = top + ddlRect.height;

        const docRight = window.innerWidth || document.documentElement.clientWidth;
        const docBottom = window.innerHeight || document.documentElement.clientHeight;

        if (ddlRight > docRight) {
            left -= ddlRight - docRight;
        }

        if (ddlBottom > docBottom) {
            top -= ddlBottom - docBottom;
        }

        const sizeObj = {
            left: Math.max(left, 0),
            top: Math.max(top, 0),
            width,
        };

        try {
            await binding.dotNetRef.invokeMethodAsync("JS_OnDropdownResize", sizeObj);
            binding.failCount = 0;
        } catch {
            // Something broke. Unregister for safety reasons.
            this.unbindDropdown(binding.dropdownID);
        }
    }

    bindDropdown(dropdownID: string, dotNetRef: DotNet.DotNetObject, proxyID: string, anchorID: string) {
        if (this.boundDropdowns.some(d => d.dropdownID === dropdownID)) {
            // Already bound.
            return;
        }

        const anchor = document.getElementById(anchorID);

        if (!anchor) {
            throw Error(`Could not find dropdown anchor with ID #${anchorID}`);
        }

        const binding: IDdlBinding = {
            dropdownID,
            proxyID,
            anchorID,
            dotNetRef,
            failCount: 0
        };

        this.observer.observe(anchor);
        this.boundDropdowns.push(binding);
        setTimeout(() => this.onResize(), 100); // Just for initial positioning just in case.
    }

    unbindDropdown(dropdownID: string) {
        let binding: IDdlBinding | null = null;

        for (let i = 0; i < this.boundDropdowns.length; ++i) {
            const db = this.boundDropdowns[i];

            if (db.dropdownID === dropdownID) {
                binding = db;
                this.boundDropdowns.splice(i, 1);
                break;
            }
        }

        if (!binding) {
            return;
        }

        const anchor = document.getElementById(binding.anchorID);
        if (anchor) {
            this.observer.unobserve(anchor);
        }
    }

    focusActiveElement(dropdownID: string) {
        const ddl = document.getElementById(dropdownID);

        if (!ddl) {
            throw Error(`Could not find a dropdown with ID ${dropdownID}`);
        }

        const targetElement = ddl.querySelector(".dropdown-item.active") as HTMLElement;
        if (!targetElement) {
            ddl.scrollTop = 0;
            return;
        }

        ddl.scrollTop = targetElement.offsetTop;
    }
}